import { useCallback, useEffect } from "react";
import { VscDebugDisconnect } from "react-icons/vsc";
import MeritoButton from "../../../../../components/common/MeritoButtons/MeritoButtons";
import StatusBadge from "../../../../../components/common/StatusBadge/StatusBadge";
import TruncateTooltip from "../../../../../components/common/TruncateTooltip/TruncateTooltip";
import { useAppDispatch, useAppSelector } from "../../../../../redux/reduxHooks";
import type { AccountRow, ChannelCatalogItem } from "../../../api/connectionHub.types";
import { ACCOUNT_ACTION } from "../../../api/connectionHub.types";
import { REQUEST_STATUS } from "../../../constants/connectionHub.constants";
import { CONNECTION_STATUS, CONNECTION_STATUS_PRESENTATION } from "../../../constants/connectionStatus.constants";
import {
  selectConnectionAccountRequest,
  selectConnectionAccounts,
} from "../../../state/connectionHub.selectors";
import { fetchConnectionAccounts } from "../../../state/connectionHub.thunks";
import AccountsModal, { AccountRemoveButton } from "../AccountsModal/AccountsModal";
import styles from "./AccountListModal.module.scss";

export interface AccountListModalProps {
  visible: boolean;
  channel: ChannelCatalogItem;
  agencyClientId: number | null;
  onConnectNew: () => void;
  onOpenAccount?: (row: AccountRow) => void;
  onRemoveAccount: (row: AccountRow) => void;
  onClose: () => void;
}

const AccountListModal = ({
  visible,
  channel,
  agencyClientId,
  onConnectNew,
  onOpenAccount,
  onRemoveAccount,
  onClose,
}: AccountListModalProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const rows = useAppSelector((state) => selectConnectionAccounts(state, channel.ipId));
  const request = useAppSelector((state) => selectConnectionAccountRequest(state, channel.ipId));

  const load = useCallback(() => {
    void dispatch(fetchConnectionAccounts({ ipId: channel.ipId, agencyClientId }));
  }, [agencyClientId, channel.ipId, dispatch]);

  useEffect(() => {
    if (visible) load();
  }, [load, visible]);

  const loading = (request.status === REQUEST_STATUS.IDLE || request.status === REQUEST_STATUS.LOADING) && !rows.length;
  const failed = request.status === REQUEST_STATUS.FAILED && !rows.length;

  return (
    <AccountsModal
      visible={visible}
      channelName={channel.ipName}
      logo={channel.logo}
      status={loading ? "loading" : failed ? "failed" : "ready"}
      errorMessage={request.error?.message}
      count={rows.length}
      emptyTitle={`Connect your ${channel.ipName} account`}
      emptyAction={
        <MeritoButton
          type="primary-1"
          size="md2"
          className={styles.connectButton}
          btnText="Connect"
          icon={<VscDebugDisconnect aria-hidden="true" />}
          onClick={onConnectNew}
        />
      }
      onRetry={load}
      onConnectNew={onConnectNew}
      onClose={onClose}
    >
      <ul className={styles.list}>
        {rows.map((row) => {
          const status = CONNECTION_STATUS_PRESENTATION[row.status];
          const name = row.connection.loginEmail ?? row.connection.ipName;
          return (
            <li className={styles.row} key={row.connection.connectionId}>
              {onOpenAccount ? (
                <button
                  type="button"
                  className={styles.rowMain}
                  disabled={row.status === CONNECTION_STATUS.CONNECTED}
                  onClick={() => onOpenAccount(row)}
                >
                  <TruncateTooltip text={name} className={styles.primary} />
                </button>
              ) : (
                <TruncateTooltip text={name} className={styles.primary} />
              )}
              <StatusBadge label={status.label} variant={status.variant} spinner={status.spinner} />
              {row.actions.includes(ACCOUNT_ACTION.REMOVE) ? (
                <AccountRemoveButton label={name} onClick={() => onRemoveAccount(row)} />
              ) : null}
            </li>
          );
        })}
      </ul>
    </AccountsModal>
  );
};

export default AccountListModal;
